import React, { useState, useEffect, useRef } from "react";
import BriefFooter from "../../components/BriefFooter";
import BrandLogo from "../../components/BrandLogo";
import { getThemeConfig } from "../../themes/themeConfig";
import "../../assets/styling/hero-redesign.css";
import "../../assets/styling/brief-interstitial.css";
import BriefEarlyAccessInterstitial from "../../components/Homepage/BriefEarlyAccessInterstitial";
import QuizContainer from "../../components/Quiz/QuizContainer";
import { useLocation } from "react-router-dom";
import { renderInlineBold } from "../../utils/text/renderInlineBold";
import { getHeroVariant } from "../../themes/content/heroVariants";
import { advertisingActive } from "../../utils/trackerGating";
import { consentHeaders } from "../../utils/consent";

// Count-up animation hook for premium micro-interaction
const useCountUp = (end, duration = 1500) => {
  const [count, setCount] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (end === 0) return;

    const startTime = performance.now();
    const startValue = 0;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic for smooth deceleration (LinkedIn/Typeform feel)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentCount = Math.floor(
        startValue + (end - startValue) * easeOut,
      );

      setCount(currentCount);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [end, duration]);

  return count;
};

const QuizHomeScreen = () => {
  const { meta, homepage } = getThemeConfig();
  const { homeHero } = meta;
  const content = homepage;
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const isInterstitialOpen = searchParams.get("early") === "1";

  // Ad-driven hero override: visitors arriving from an ad with a known
  // utm_content slug see a hero whose H1/subhead matches the ad copy they
  // clicked. Unknown / missing slugs fall back to the default theme hero.
  const adVariant = getHeroVariant(searchParams.get("utm_content"));
  const hero = adVariant ? { ...content.hero, ...adVariant } : content.hero;

  const quizRequest = content.quizRequest || {};
  const funnelParams = {
    flow: "brief_early_access",
    city: quizRequest.cityLabel || meta?.brandLogo?.topText || "San Antonio",
    city_slug: quizRequest.citySlug || "sanantonio",
    persona: quizRequest.persona || "marketing_rep",
    quizSlug: quizRequest.quizSlug || "sanantonio_marketing_rep_v1",
  };

  // Quiz overlay state
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizLoadError, setQuizLoadError] = useState("");

  // Social proof: start with theme fallback, upgrade to live data if available.
  const [quizCount, setQuizCount] = useState(0);
  const [rating, setRating] = useState(0);

  useEffect(() => {
    // Parse initial theme config values
    const initialStats = content.socialProof?.stats || [];
    if (initialStats[0]?.value) {
      const parsedCount = parseInt(
        initialStats[0].value.replace(/[^0-9]/g, ""),
        10,
      );
      setQuizCount(parsedCount || 100);
    }
    if (initialStats[1]?.value) {
      const parsedRating = parseFloat(
        initialStats[1].value.replace(/[^0-9.]/g, ""),
      );
      setRating(parsedRating || 4.5);
    }

    // Fetch live stats from backend
    fetch(`/api/analytics/stats?city=${funnelParams.city_slug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.quizCount) {
          setQuizCount(data.quizCount);
          setRating(data.avgRating || 4.5);
        }
      })
      .catch(() => {}); // keep theme fallback on error
  }, [content.socialProof?.stats, funnelParams.city_slug]);

  // Count-up animations (triggered on page load)
  const animatedQuizCount = useCountUp(quizCount, 1600);
  const animatedRating = useCountUp(Math.round(rating * 10), 1400);

  // Format display values
  const quizCountText = `${animatedQuizCount.toLocaleString()}+`;
  const ratingText = `${(animatedRating / 10).toFixed(1)}★`;

  const handleCtaClick = async () => {
    if (quizLoading) return;
    const eventId = crypto.randomUUID();
    const customData = {
      stage: "cta_click",
      flow: funnelParams.flow,
      city: funnelParams.city,
      city_slug: funnelParams.city_slug,
      persona: funnelParams.persona,
    };

    // Advertising events — browser pixel and the matching server-side event — only
    // run with advertising consent. Both are gated on the same check so rejecting
    // in the browser cannot be sidestepped by the server-side path.
    if (advertisingActive()) {
      window.fbq("track", "Lead", customData, { eventID: eventId });

      // Server-side Meta CAPI (fire-and-forget)
      fetch("/api/meta/capi", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...consentHeaders() },
        keepalive: true,
        body: JSON.stringify({
          event_name: "Lead",
          event_id: eventId,
          custom_data: customData,
        }),
      }).catch(() => {});
    }

    // Quiz analytics (fire-and-forget)
    fetch("/api/analytics/quiz-start", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        city: funnelParams.city_slug,
        persona: funnelParams.persona,
        sessionId: eventId,
      }),
    }).catch(() => {});

    setQuizLoadError("");
    setQuizLoading(true);
    try {
      const response = await fetch(
        `/api/quiz/content/${encodeURIComponent(funnelParams.quizSlug)}`,
      );
      if (!response.ok) throw new Error("quiz_unavailable");
      const data = await response.json();
      if (!Array.isArray(data?.questions) || data.questions.length === 0) {
        throw new Error("quiz_invalid");
      }
      setQuizData(data);
      setQuizOpen(true);
    } catch {
      setQuizLoadError("Quiz is temporarily unavailable. Please try again.");
    } finally {
      setQuizLoading(false);
    }
  };

  return (
    <div className="tbb-full-bleed">
      <BriefEarlyAccessInterstitial
        open={isInterstitialOpen}
        submitUrl={content?.interstitial?.submitUrl}
        cityLabel={meta?.brandLogo?.topText || ""}
        content={content?.interstitial}
        trackingFlow={funnelParams.flow}
        trackingCity={funnelParams.city}
        trackingCitySlug={funnelParams.city_slug}
        trackingPersona={funnelParams.persona}
      />

      {quizOpen && quizData && (
        <QuizContainer
          questions={quizData.questions}
          city={funnelParams.city_slug}
          persona={funnelParams.persona}
          quizSlug={funnelParams.quizSlug}
        />
      )}

      {/* Hero Section with Image/Video */}
      <div className="hero-redesign">
        {/* Background Image/Video */}
        <div className="hero-redesign__media">
          {homeHero?.kind === "image" ? (
            <img
              className="hero-redesign__image"
              src={homeHero.src}
              alt=""
              aria-hidden="true"
            />
          ) : (
            <video
              className="hero-redesign__video"
              src={homeHero?.src}
              poster={homeHero?.poster}
              autoPlay
              loop
              muted
              playsInline
            />
          )}
        </div>

        {/* Logo at top-left */}
        <div className="hero-redesign__logo">
          <BrandLogo />
        </div>

        {/* White gradient with content at bottom */}
        <div className="hero-redesign__gradient">
          <div className="hero-redesign__content">
            <h1 className="hero-redesign__title">
              {hero.titleLines?.map((line, idx) => (
                <React.Fragment key={idx}>
                  {line.text}
                  {line.highlight && (
                    <span className="hero-redesign__highlight">
                      {line.highlight}
                    </span>
                  )}
                  {idx < hero.titleLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </h1>
            <p className="hero-redesign__subtitle">
              {renderInlineBold(
                (hero.subhead || []).filter(Boolean).join(" "),
              )}
            </p>
            <button
              type="button"
              className="hero-redesign__cta"
              onClick={handleCtaClick}
              disabled={quizLoading}
            >
              {quizLoading
                ? "Loading quiz..."
                : renderInlineBold(hero.ctaLabel)}
            </button>
            {quizLoadError && (
              <p className="hero-redesign__subtitle">{quizLoadError}</p>
            )}
          </div>
        </div>
      </div>

      {/* Social Proof Stats */}
      <section className="social-proof" aria-label="Social proof statistics">
        <div className="social-proof__grid">
          <div className="social-proof__visual" aria-hidden="true">
            <img
              src="https://d2oa23hr7q6t96.cloudfront.net/media/participants.png"
              alt=""
            />
          </div>

          <div className="social-proof__stat social-proof__stat--taken">
            <span className="social-proof__value">{quizCountText}</span>
            <span className="social-proof__label">Quizzes Taken</span>
          </div>

          <div className="social-proof__stat social-proof__stat--rating">
            <span className="social-proof__value social-proof__value--rating">
              {ratingText}
            </span>
            <span className="social-proof__label">Average Rating</span>
          </div>
        </div>
      </section>

      <BriefFooter />
    </div>
  );
};

export default QuizHomeScreen;
